import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Segment, Grid, Icon, List, Divider, Popup } from "semantic-ui-react";
import { Airline } from "src/components";
import { getFlightStatus } from "src/services/application";
import { formatTime, toSpinalCase } from "src/common";
import { AccessibleDate } from "src/components/AccessibleDate";
import { useFocusRefOnLoad } from "src/common/hooks";
import { FlightStatusEnumI } from "src/redux/reducers/application";
import { Passenger } from "src/types/Passenger";
import PlaneIcon from "src/icons/PlaneIcon";
import EmailIcon from "src/icons/EmailIcon";
import { FlightSegment } from "src/types/Flight";
import { AccessibleIdentifier } from "../AccessibleIdentifier";
import { useTranslate } from "@tolgee/react";

const riskToClass = {
  1: "lowestRisk",
  2: "lowRisk",
  3: "mediumRisk",
  4: "highRisk",
  5: "severeRisk",
};

type City = {
  airportCode: string;
  state: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  utcTime: string;
  localTime: string;
  localEstimatedTime: string;
  highestRiskLevel?: number;
};

const PopupContent = ({
  departureCity,
  arrivalCity,
  passengers,
  setOpen,
}: {
  departureCity: City;
  arrivalCity: City;
  passengers: Passenger[];
  setOpen: (val: boolean) => void;
}) => {
  const { t } = useTranslate();
  const emailRef = useFocusRefOnLoad();
  const containerRef = useRef(null);

  useEffect(() => {
    function listener({ relatedTarget }) {
      if (
        containerRef.current &&
        !containerRef.current.contains(relatedTarget)
      ) {
        setOpen(false);
      }
    }
    window.addEventListener("focusout", listener, true);
    return () => window.removeEventListener("focusout", listener);
  }, [setOpen]);

  let emailFound;

  const getEmail = (p) => {
    if (p.email) {
      const ref = emailFound ? null : emailRef;
      emailFound = true;
      return (
        <a href={`mailto:${p.email}`} ref={ref} target="_blank">
          <EmailIcon width="12px" aria-hidden />
        </a>
      );
    } else return null;
  };
  return (
    <div className="todays-flights-popup" ref={containerRef}>
      <h4>
        {t({
          key: "components.segmentCards.flight.flightSummary",
          params: {
            departureCity: departureCity.city,
            arrivalCity: arrivalCity.city,
          },
          defaultValue: "{departureCity} to {arrivalCity}",
        })}
      </h4>
      <ul>
        {passengers.map((p, i) => (
          <li key={p.name} data-aut={`FlightCard|Popup|Passenger${i}`}>
            {p.name} {getEmail(p)}
          </li>
        ))}
      </ul>
    </div>
  );
};

const FlightPopup = ({
  departure,
  arrival,
  passengers,
}: {
  departure: City;
  arrival: City;
  passengers: Passenger[];
}) => {
  const { t } = useTranslate();
  const [isOpen, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);
  return (
    <Popup
      trigger={
        <button
          className="unstyled-button"
          aria-label={t({
            key: "components.segmentCards.flight.popupAriaLabel",
            defaultValue: "Traveler List Popup",
          })}
        >
          <Icon name="travel" aria-hidden="true" />
        </button>
      }
      on="click"
      open={isOpen}
      onClose={handleClose}
      onOpen={handleOpen}
      position="left center"
    >
      <Popup.Content>
        <PopupContent
          departureCity={departure}
          arrivalCity={arrival}
          passengers={passengers}
          setOpen={setOpen}
        />
      </Popup.Content>
    </Popup>
  );
};

interface FlightCardI {
  operatingFlightNumber: string;
  carrier: string;
  arrival: City;
  departure: City;
  displayRisk?: boolean;
  passengers?: Passenger[];
  isRiskCard?: boolean;
  isSelected?: boolean;
  flight: FlightSegment;
  passenger: Passenger;
  renderPopup?: boolean;
  flightStatusEnum: FlightStatusEnumI;
  showDebtorName?: boolean;
  onCardClick: () => void;
}

const FlightCard = ({
  operatingFlightNumber,
  carrier,
  arrival,
  departure,
  displayRisk,
  passengers,
  isRiskCard,
  isSelected,
  flight,
  passenger,
  renderPopup,
  flightStatusEnum,
  showDebtorName,
  onCardClick,
}: FlightCardI) => {
  const { t } = useTranslate();
  const getStatus = (flight) => {
    const statusLabel = getFlightStatus(flight.flightStatus, flightStatusEnum);
    const status = toSpinalCase(statusLabel);
    return (
      <List.Item
        data-aut="FlightCard|FlightStatus"
        className={`status ${status}`}
      >
        {statusLabel}
        <br />
      </List.Item>
    );
  };

  const renderPassengers = () => {
    return passenger
      ? passenger.name
      : passengers.map((p) => p.name).join(", ");
  };

  const renderRisk = (rating: number) => {
    let riskText, riskClass;
    switch (rating) {
      case 5:
        riskText = t({
          key: "components.segmentCards.flight.extreme",
          defaultValue: "Extreme",
        });
        riskClass = "extreme";
        break;
      case 4:
        riskText = t({
          key: "components.segmentCards.flight.high",
          defaultValue: "High",
        });
        riskClass = "high";
        break;
      case 3:
        riskText = t({
          key: "components.segmentCards.flight.moderate",
          defaultValue: "Moderate",
        });
        riskClass = "moderate";
        break;
      case 2:
        riskText = t({
          key: "components.segmentCards.flight.low",
          defaultValue: "Low",
        });
        riskClass = "low";
        break;
      case 1:
        riskText = t({
          key: "components.segmentCards.flight.trivial",
          defaultValue: "Trivial",
        });
        riskClass = "trivial";
        break;
      default:
        return;
    }
    return (
      <List.Item
        data-aut="FlightCard|FlightRisk"
        className={`risk-text ${riskClass}`}
      >
        <span className={`risk-text extreme`}>
          {t({
            key: "components.segmentCards.flight.risk",
            defaultValue: "Risk:",
          })}
        </span>{" "}
        {riskText}
      </List.Item>
    );
  };

  const flightCardClass = (isRiskCard && riskToClass[flight.riskLevel]) || "";
  let riskRating = 0;
  if (displayRisk) {
    if (departure.highestRiskLevel) riskRating = departure.highestRiskLevel;
    if (arrival.highestRiskLevel && arrival.highestRiskLevel > riskRating)
      riskRating = arrival.highestRiskLevel;
  }

  return (
    <div style={{ position: "relative" as const }}>
      <Segment
        className={`flight-card ${flightCardClass}${
          isSelected ? " selected" : ""
        }`}
        key={operatingFlightNumber}
        onClick={() => onCardClick()}
        role="button"
        tabIndex={0}
        ariaLabel={t({
          key: "components.segmentCards.flight.ariaLabel",
          defaultValue:
            "View details for {carrier} flight {operatingFlightNumber} from {departureAirportCode} to {arrivalAirportCode}",
          params: {
            carrier,
            operatingFlightNumber,
            departureAirportCode: departure.airportCode,
            arrivalAirportCode: arrival.airportCode,
          },
        })}
      >
        <div className="heading">
          <List horizontal className="flight-info-list">
            <List.Item>
              <Airline code={carrier} />
            </List.Item>
            <List.Item className="ml8">
              <b data-aut="FlightCard|CarrierFightNumber">
                {carrier} {operatingFlightNumber}
              </b>
            </List.Item>
            <List.Item className="ml8">
              <b data-aut="FlightCard|DepartureAirportCode">
                {departure.airportCode}
              </b>
            </List.Item>
            <List.Item className="ml8">
              <PlaneIcon width="20px" height="20px" color="gray" />
            </List.Item>
            <List.Item className="ml8">
              <b data-aut="FlightCard|ArrivalAirportCode">
                {arrival.airportCode}
              </b>
            </List.Item>
          </List>
          <List
            className="flight-card-top-right-corner"
            horizontal
            floated="right"
          >
            {/* This is for the upcoming flights version of the flightcard */}
            {renderPopup && (
              <List.Item>
                <FlightPopup
                  departure={departure}
                  arrival={arrival}
                  passengers={passengers}
                />
                {passengers.length}
              </List.Item>
            )}
          </List>
        </div>
        <div className="flight-card-checkin-row"></div>
        <div className="flight-card-passengers-container">
          <span data-aut="FlightCard|Passengers">{renderPassengers()}</span>
          {passenger.pnr && (
            <span className="pnr" data-aut="FlightCard|PNR">
              <AccessibleIdentifier identifier={passenger.pnr} />
            </span>
          )}
        </div>
        {showDebtorName && flight.debtorName && (
          <span
            className="flight-card-passengers-container"
            data-aut="FlightCard|CompanyName"
            style={{
              display: "block",
              textAlign: "left",
              marginTop: "4px",
              visibility: flight.debtorName ? "visible" : "hidden",
            }}
          >
            {t({
              key: "components.segmentCards.company",
              defaultValue: "Company: {name}",
              params: { name: flight.debtorName },
            })}
          </span>
        )}
        <div>
          <Divider className="flight-card-divider" />
        </div>
        <Grid>
          <Grid.Row columns={3}>
            <Grid.Column className="flight-info-grid-column">
              <List size="tiny">
                <List.Item>
                  <List.Header>
                    {t({
                      key: "components.segmentCards.flight.departure",
                      defaultValue: "Departure",
                    })}
                  </List.Header>
                </List.Item>
                <List.Item data-aut="FlightCard|DepartureDate">
                  <AccessibleDate date={departure.localTime} />
                </List.Item>
                <List.Item data-aut="FlightCard|DepartureTime">
                  {formatTime(departure.localTime)}
                </List.Item>
              </List>
            </Grid.Column>
            <Grid.Column className="flight-info-grid-column">
              <List size="tiny">
                <List.Item>
                  <List.Header>
                    {t({
                      key: "components.segmentCards.flight.arrival",
                      defaultValue: "Arrival",
                    })}
                  </List.Header>
                </List.Item>
                <List.Item data-aut="FlightCard|ArrivalDate">
                  <AccessibleDate date={arrival.localTime} />
                </List.Item>
                <List.Item data-aut="FlightCard|ArrivalTime">
                  {formatTime(arrival.localTime)}
                </List.Item>
              </List>
            </Grid.Column>
            <Grid.Column className="flight-info-grid-column">
              <List size="tiny">
                <List.Item>
                  <List.Header>
                    {t({
                      key: "components.segmentCards.flight.status",
                      defaultValue: "Status",
                    })}
                  </List.Header>
                </List.Item>
                {getStatus(flight)}
                {riskRating > 0 ? renderRisk(riskRating) : null}
              </List>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>
      {passenger && passenger.email && (
        <div
          className="passenger-email"
          style={{
            position: "absolute",
            top: "24px",
            right: "18px",
            cursor: "pointer",
          }}
        >
          <span
            onClick={(e) => {
              e.stopPropagation();
              window.location.href = `mailto:${passenger.email}`;
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                e.preventDefault();
                window.location.href = `mailto:${passenger.email}`;
              }
            }}
            role="link"
            tabIndex={0}
            className="passenger-email-link"
            aria-label={t({
              key: "components.segmentCards.flight.emailAriaLabel",
              defaultValue: "Email: {name}",
              params: { name: passenger.name },
            })}
          >
            <EmailIcon />
          </span>
        </div>
      )}
      {flight.checkInUrl && flight.referenceNumber && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            window.open(flight.checkInUrl, "_blank", "noreferrer");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              e.preventDefault();
              window.open(flight.checkInUrl, "_blank", "noreferrer");
            }
          }}
          role="link"
          tabIndex={0}
          className="flight-card-passengers-checkin"
        >
          {t({
            key: "components.segmentCards.flight.checkIn",
            defaultValue: "Check In",
          })}
        </span>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  flightStatusEnum: state.application.flightStatusEnum,
});

export default connect(mapStateToProps)(FlightCard);
